// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

// live-signal — the live indicator / ticker / completion-notification
// module (PRD §Modules `live-signal`; issue `live-signal-notification`,
// US §5 §6 §7).
//
// Contract (PRD §Interfaces "Web Notification"):
//   - fired only on an **observed** `running -> terminal` transition
//     during the session — never on first load of an already-terminal
//     cycle;
//   - permission requested only on an explicit user gesture;
//   - denied / unavailable permission degrades silently (no throw, no
//     console error).
//
// This module owns the two pure/boundary primitives; the dashboard
// component (`views/evolutionCycles/dashboard/LiveSignal`) wires them
// to the polled `useEvolutionCycle` status kept fresh by
// `refresh-policy` (INV-3 — no fetching inside this module).

import { isTerminal } from "./refresh-policy"
import type { RawFeedEvent } from "./event-feed-curation"

// True only on an observed `running -> terminal` transition (ADR-0022).
// `prevStatus === undefined` means nothing observed yet this session —
// a first load landing on an already-terminal status is not a transition
// (AC1). Terminal->terminal or running->running are also no-ops.
export const detectTransition = (
  prevStatus: CycleStatus | undefined,
  status: CycleStatus | undefined,
): boolean => prevStatus === "running" && isTerminal(status)

// `now` is injected for testability — never `Date.now()` inside.
// Independent of `cycle-health`'s own last-event-age derivation:
// `live-signal`'s only declared dependency is `event-feed-curation`
// (PRD §Modules), not `cycle-health`.
export const lastEventAgeMs = (
  events: readonly RawFeedEvent[],
  now: number,
): number | null => {
  const timestamps = events
    .map((e) => Date.parse(e.occurred_at))
    .filter((ms) => Number.isFinite(ms))
  return timestamps.length > 0 ? now - Math.max(...timestamps) : null
}

const hasNotificationApi = (): boolean =>
  typeof window !== "undefined" && typeof window.Notification !== "undefined"

// Returns `"denied"` when the API is unavailable (no throw).
// Never requests permission itself — that is `requestNotificationPermission`,
// which must be called only from an explicit user gesture.
export const currentNotificationPermission = (): NotificationPermission =>
  hasNotificationApi() ? window.Notification.permission : "denied"

// Degrades silently (no throw, no console error) when the API is absent
// or permission is not "granted" (AC3). A Notification constructor that
// throws despite the API existing — observed in some restricted contexts —
// degrades the same way.
export const notify = (title: string, options?: NotificationOptions): void => {
  if (!hasNotificationApi()) return
  if (window.Notification.permission !== "granted") return
  try {
    new window.Notification(title, options)
  } catch {
    // Silent degrade — constructor can throw in restricted contexts (see comment above).
  }
}

// Callers MUST invoke only from an explicit user gesture (PRD §Interfaces);
// this module never calls it itself. Never throws — resolves `"denied"`
// when the API is unavailable or the browser call rejects.
export const requestNotificationPermission =
  async (): Promise<NotificationPermission> => {
    if (!hasNotificationApi()) return "denied"
    try {
      return await window.Notification.requestPermission()
    } catch {
      return "denied"
    }
  }
